'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import Image from 'next/image';

interface HeaderProps {
  currentLang?: 'sl' | 'en';
}

export default function Header({ currentLang = 'sl' }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const pathname = usePathname();
  const supabase = createClient();
  const { currentLang: contextLang, setLanguage } = useLanguage();

  // Use context language instead of prop
  const activeLang = contextLang;

  // Check if current page is the landing page
  const isLandingPage = pathname === '/homepage';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setIsAdmin(!!user);
    };
    checkAdminStatus();
  }, [supabase]);

  const languages = [
    { code: 'sl', name: 'Slovenščina', flag: 'SL' },
    { code: 'en', name: 'English', flag: 'EN' },
    // { code: 'fr', name: 'Français', flag: 'FR' },
    // { code: 'de', name: 'Deutsch', flag: 'DE' },
    // { code: 'it', name: 'Italiano', flag: 'IT' },
    // { code: 'uk', name: 'Українська', flag: 'UK' },
    // { code: 'ru', name: 'Русский', flag: 'RU' },
  ];

  const currentLanguage = languages.find((lang) => lang.code === activeLang) || languages[0];

  const navLabels: Record<string, Record<string, string>> = {
    home: {
      sl: 'Domov',
      en: 'Home',
      fr: 'Accueil',
      de: 'Startseite',
      it: 'Home',
      uk: 'Головна',
      ru: 'Главная',
    },
    products: {
      sl: 'Produkti',
      en: 'Products',
      fr: 'Produits',
      de: 'Produkte',
      it: 'Prodotti',
      uk: 'Продукти',
      ru: 'Продукты',
    },
    services: {
      sl: 'Storitve',
      en: 'Services',
      fr: 'Services',
      de: 'Dienstleistungen',
      it: 'Servizi',
      uk: 'Послуги',
      ru: 'Услуги',
    },
    blog: { sl: 'Blog', en: 'Blog', fr: 'Blog', de: 'Blog', it: 'Blog', uk: 'Блог', ru: 'Блог' },
    contact: {
      sl: 'Kontakt',
      en: 'Contact',
      fr: 'Contact',
      de: 'Kontakt',
      it: 'Contatto',
      uk: 'Контакт',
      ru: 'Контакт',
    },
    admin: {
      sl: 'Admin',
      en: 'Admin',
      fr: 'Admin',
      de: 'Admin',
      it: 'Admin',
      uk: 'Адмін',
      ru: 'Админ',
    },
  };

  const navLinks = [
    {
      id: 'nav_home',
      href: '/homepage',
      label: navLabels.home[activeLang],
    },
    {
      id: 'nav_products',
      href: '/products',
      label: navLabels.products[activeLang],
    },
    {
      id: 'nav_services',
      href: '/services',
      label: navLabels.services[activeLang],
    },
    {
      id: 'nav_blog',
      href: '/blog',
      label: navLabels.blog[activeLang],
    },
    {
      id: 'nav_contact',
      href: '/contact',
      label: navLabels.contact[activeLang],
    },
  ];

  if (isAdmin) {
    navLinks.push({
      id: 'nav_admin',
      href: '/admin',
      label: navLabels.admin[activeLang],
    });
  }

  const isActive = (href: string) => pathname === href;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 p-6 md:p-6 flex items-center justify-between transition-all duration-500 ${
        isScrolled || !isLandingPage
          ? 'bg-black/80 backdrop-blur-md border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      {/* Logo */}
      <Link
        href="/homepage"
        className="relative w-16 h-16 md:w-24 md:h-24 transition-opacity duration-300 hover:opacity-80"
      >
        <Image src="/logo.webp" fill alt="logo" />
      </Link>

      {/* Desktop Navigation */}
      <div
        className={`hidden md:flex items-center gap-2 p-1 rounded-full border transition-all duration-300 ${
          isScrolled
            ? 'bg-white/10 backdrop-blur-md border-white/10'
            : 'bg-white/10 backdrop-blur-md border-white/10'
        }`}
      >
        {navLinks.map((link) => (
          <Link
            key={link.id}
            href={link.href}
            className={`px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${
              isActive(link.href)
                ? 'bg-primary text-white shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      {/* Language Switcher & Mobile Menu */}
      <div className="flex items-center gap-4">
        {/* Language Switcher Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/10 transition-all"
          >
            <span>{currentLanguage.flag}</span>
            <Icon
              name="ChevronDownIcon"
              size={16}
              className={`transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isLangDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-black/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code as any);
                    setIsLangDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    activeLang === lang.code
                      ? 'bg-white/20 text-white font-bold'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="font-medium">{lang.flag}</span> {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-3 bg-white/20 backdrop-blur-md rounded-full text-white border border-white/10"
          aria-label="Toggle mobile menu"
        >
          <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-lg z-[60] md:hidden min-h-screen">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 p-3 bg-white/20 backdrop-blur-md rounded-full text-white border border-white/20"
            aria-label="Close mobile menu"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
          <div className="flex flex-col items-center justify-center h-full gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-2xl font-bold uppercase tracking-widest transition-colors ${
                  isActive(link.href) ? 'text-primary' : 'text-white hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
